import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertCircle, Calendar, Clock, TrendingUp, Users, 
  User, Eye, Filter, Gift, Thermometer, CalendarDays,
  ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle 
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WarehouseAnalytics = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [detailType, setDetailType] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // Filter state
  const [dateFilter, setDateFilter] = useState('month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState('all');
  const [employees, setEmployees] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [dateFilter, customStartDate, customEndDate, selectedEmployeeFilter]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/employees?department=Warehouse`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.filter(emp => emp.department === 'Warehouse' && emp.active !== false));
      }
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      let url = `${BACKEND_URL}/api/warehouse-analytics?date_filter=${dateFilter}`;
      
      if (dateFilter === 'custom' && customStartDate && customEndDate) {
        url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
      }
      
      if (selectedEmployeeFilter !== 'all') {
        url += `&employee_id=${selectedEmployeeFilter}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch analytics');

      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const openDetailDialog = (type, employee) => {
    setDetailType(type);
    setSelectedEmployee(employee);
    setShowDetailDialog(true);
  };

  const getBradfordColor = (score) => {
    if (score >= 500) return 'text-red-600 bg-red-100';
    if (score >= 200) return 'text-orange-600 bg-orange-100';
    return 'text-yellow-600 bg-yellow-100';
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === 'Ongoing') return dateStr;
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDateFilterLabel = () => {
    switch (dateFilter) {
      case 'today': return 'Today';
      case 'week': return 'Week to Date';
      case 'month': return 'This Month';
      case 'custom': return customStartDate && customEndDate 
        ? `${formatDate(customStartDate)} - ${formatDate(customEndDate)}` 
        : 'Custom Range';
      default: return 'This Month';
    }
  };

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading analytics...</div>
      </div>
    );
  }

  const summary = analytics?.summary || {};

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Warehouse Analytics</h1>
          <p className="text-sm text-gray-600 mt-1">Team overview and performance metrics</p>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2"
        >
          <Filter className="w-4 h-4" />
          Filters
          <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </Button>
      </div>

      {/* Filters Section */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-md p-4 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Date Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Select date range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">Week to Date</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Custom Date Range */}
            {dateFilter === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
              </>
            )}

            {/* Employee Filter */}
            <div className={dateFilter === 'custom' ? 'md:col-span-3' : ''}>
              <label className="block text-sm font-medium text-gray-700 mb-2">Employee</label>
              <Select value={selectedEmployeeFilter} onValueChange={setSelectedEmployeeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Warehouse Employees</SelectItem>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Active Filter Display */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-600">
              Showing: <span className="font-medium">{getDateFilterLabel()}</span>
              {selectedEmployeeFilter !== 'all' && (
                <span> • <span className="font-medium">
                  {employees.find(e => e.id === selectedEmployeeFilter)?.first_name} {employees.find(e => e.id === selectedEmployeeFilter)?.last_name}
                </span></span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Summary Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-red-500">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-red-500" />
            <span className="text-sm text-gray-600">Late Arrivals</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{summary.total_late_instances || 0}</p>
          <p className="text-xs text-gray-500">{summary.total_late_employees || 0} employees</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-orange-500">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-orange-500" />
            <span className="text-sm text-gray-600">Absences</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{summary.total_absences || 0}</p>
          <p className="text-xs text-gray-500">Currently absent</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-pink-500">
          <div className="flex items-center gap-2 mb-2">
            <Thermometer className="w-5 h-5 text-pink-500" />
            <span className="text-sm text-gray-600">Sickness</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{summary.total_sickness || 0}</p>
          <p className="text-xs text-gray-500">In period</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-amber-500">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-amber-500" />
            <span className="text-sm text-gray-600">Bradford Alerts</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{summary.total_bradford_alerts || 0}</p>
          <p className="text-xs text-gray-500">Above threshold</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-blue-500">
          <div className="flex items-center gap-2 mb-2">
            <CalendarDays className="w-5 h-5 text-blue-500" />
            <span className="text-sm text-gray-600">Upcoming Leave</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{summary.total_upcoming_leave || 0}</p>
          <p className="text-xs text-gray-500">Next 30 days</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-purple-500">
          <div className="flex items-center gap-2 mb-2">
            <Gift className="w-5 h-5 text-purple-500" />
            <span className="text-sm text-gray-600">Birthdays</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{summary.total_birthdays || 0}</p>
          <p className="text-xs text-gray-500">Next 30 days</p>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Late Arrivals */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-red-600" />
              <h2 className="text-lg font-semibold">Late Arrivals</h2>
              <span className="ml-auto bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-semibold">
                {analytics?.late_arrivals?.length || 0}
              </span>
            </div>

            {analytics?.late_arrivals?.length === 0 ? (
              <p className="text-gray-500 text-sm">No late arrivals in this period ✓</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {analytics?.late_arrivals?.map((emp) => (
                  <div key={emp.employee_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{emp.name}</p>
                      <p className="text-sm text-gray-600">Late {emp.late_count} times</p>
                    </div>
                    <div className="flex gap-2 ml-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDetailDialog('late', emp)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => navigate(`/admin/employees/${emp.employee_id}`)}
                      >
                        <User className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sickness Records */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Thermometer className="w-5 h-5 text-pink-600" />
              <h2 className="text-lg font-semibold">Sickness Records</h2>
              <span className="ml-auto bg-pink-100 text-pink-700 px-3 py-1 rounded-full text-sm font-semibold">
                {analytics?.sickness_records?.length || 0}
              </span>
            </div>

            {analytics?.sickness_records?.length === 0 ? (
              <p className="text-gray-500 text-sm">No sickness records in this period ✓</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {analytics?.sickness_records?.slice(0, 10).map((record, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {employees.find(e => e.id === record.employee_id)?.first_name || 'Employee'} {employees.find(e => e.id === record.employee_id)?.last_name || ''}
                      </p>
                      <p className="text-sm text-gray-600">
                        {formatDate(record.first_day_absent)} • {record.total_days || 1} days
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      record.status === 'ongoing' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {record.status || 'Completed'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming Time Off */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold">Upcoming Time Off</h2>
              <span className="ml-auto bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">
                {analytics?.upcoming_time_off?.length || 0}
              </span>
            </div>

            {analytics?.upcoming_time_off?.length === 0 ? (
              <p className="text-gray-500 text-sm">No upcoming time off scheduled</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {analytics?.upcoming_time_off?.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{item.name}</p>
                      <p className="text-sm text-gray-600">
                        {formatDate(item.start_date)} - {formatDate(item.end_date)} ({item.days} {item.days === 1 ? 'day' : 'days'})
                      </p>
                    </div>
                    <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-700">
                      {item.leave_type}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Current Absences */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              <h2 className="text-lg font-semibold">Current Absences</h2>
              <span className="ml-auto bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-semibold">
                {analytics?.current_absences?.length || 0}
              </span>
            </div>

            {analytics?.current_absences?.length === 0 ? (
              <p className="text-gray-500 text-sm">No current absences ✓</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {analytics?.current_absences?.map((emp) => (
                  <div key={emp.employee_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{emp.name}</p>
                      <p className="text-sm text-gray-600">
                        {emp.leave_type} • Since {formatDate(emp.start_date)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/admin/employees/${emp.employee_id}`)}
                    >
                      <User className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bradford Factor Alerts */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-amber-600" />
              <h2 className="text-lg font-semibold">Bradford Factor Alerts</h2>
              <span className="ml-auto bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-semibold">
                {analytics?.bradford_factor_alerts?.length || 0}
              </span>
            </div>

            {analytics?.bradford_factor_alerts?.length === 0 ? (
              <p className="text-gray-500 text-sm">No Bradford Factor alerts ✓</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {analytics?.bradford_factor_alerts?.map((emp) => (
                  <div key={emp.employee_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium truncate">{emp.name}</p>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getBradfordColor(emp.bradford_score)}`}>
                          {emp.bradford_score}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        {emp.absence_spells} spells • {emp.total_days_absent} days
                      </p>
                    </div>
                    <div className="flex gap-2 ml-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDetailDialog('bradford', emp)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => navigate(`/admin/employees/${emp.employee_id}`)}
                      >
                        <User className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming Birthdays */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Gift className="w-5 h-5 text-purple-600" />
              <h2 className="text-lg font-semibold">Upcoming Birthdays</h2>
              <span className="ml-auto bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-semibold">
                {analytics?.upcoming_birthdays?.length || 0}
              </span>
            </div>

            {analytics?.upcoming_birthdays?.length === 0 ? (
              <p className="text-gray-500 text-sm">No upcoming birthdays</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {analytics?.upcoming_birthdays?.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{item.name}</p>
                      <p className="text-sm text-gray-600">
                        {formatDate(item.birthday)} • {item.days_until === 0 ? 'Today! 🎉' : `In ${item.days_until} days`}
                      </p>
                    </div>
                    {item.days_until === 0 && (
                      <span className="text-2xl">🎂</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {detailType === 'late' && 'Late Arrival Details'}
              {detailType === 'bradford' && 'Absence History'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {selectedEmployee && (
              <>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-semibold text-lg">{selectedEmployee.name}</p>
                  <p className="text-sm text-gray-600">{selectedEmployee.email}</p>
                </div>

                {detailType === 'late' && selectedEmployee.late_instances && (
                  <div className="space-y-2">
                    <h3 className="font-semibold">Late Arrivals ({selectedEmployee.late_count})</h3>
                    {selectedEmployee.late_instances.map((instance, idx) => (
                      <div key={idx} className="p-3 border rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{formatDate(instance.date)}</p>
                            <p className="text-sm text-gray-600">
                              Expected: {instance.expected} • Clocked in: {instance.clock_in}
                            </p>
                          </div>
                          <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-sm font-semibold">
                            {instance.minutes_late} min late
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {detailType === 'bradford' && selectedEmployee.absences && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold">Absence History (Last 52 Weeks)</h3>
                      <span className={`px-3 py-1 rounded-lg text-sm font-semibold ${getBradfordColor(selectedEmployee.bradford_score)}`}>
                        Bradford Score: {selectedEmployee.bradford_score}
                      </span>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg text-sm">
                      <p><strong>Total Absence Periods:</strong> {selectedEmployee.absence_spells}</p>
                      <p><strong>Total Days Absent:</strong> {selectedEmployee.total_days_absent}</p>
                    </div>
                    {selectedEmployee.absences.map((absence, idx) => (
                      <div key={idx} className="p-3 border rounded-lg">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium">{absence.type}</p>
                            <p className="text-sm text-gray-600">
                              {formatDate(absence.start_date)} - {formatDate(absence.end_date)} ({absence.days} {absence.days === 1 ? 'day' : 'days'})
                            </p>
                            {absence.reason && (
                              <p className="text-sm text-gray-500 mt-1">Reason: {absence.reason}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WarehouseAnalytics;
